import { HomeCmsContent } from '../models/types';

export const HOME_CMS_IDENTIFIER = 'home-page';

export const DEFAULT_HOME_CMS: HomeCmsContent = {
  hero: {
    title: 'TRADE. EARN.',
    highlight: 'GROW.',
    description:
      'The next generation decentralized exchange. Secure, fast and built for the future of DeFi.',
  },
  ecosystem: [
    {
      title: 'Swap Core',
      description: 'Instant asset trading across multiple networks.',
    },
    {
      title: 'Liquidity',
      description: 'Contribute LP capital and capture trade fee commissions.',
    },
    {
      title: 'Yield Farms',
      description: 'Stake LP tokens to harvest compounding high APR yields.',
    },
    {
      title: 'Token Staking',
      description: 'Stake tokens directly to earn ecosystem revenues.',
    },
  ],
  stakeBanner: {
    title: 'Stake Tokens. Earn More.',
    description:
      'Locked staking grants high compounding multiplier returns up to 1.5x on liquid yields.',
  },
  walletBanner: {
    title: 'Interactive Wallet Cockpit',
    description:
      'Securely link your cryptocurrency wallets to view real-time pool balances and rewards.',
  },
  features: [
    {
      title: 'Fully Audited',
      description:
        'Smart contracts audited by leading security agencies CertiK & PeckShield.',
    },
    {
      title: 'Lowest Payout Fees',
      description: 'Highly optimized smart contracts ensure minimal gas fees for swap actions.',
    },
    {
      title: 'Instant Execution',
      description: 'High performance blockchain infrastructure triggers transactions instantly.',
    },
    {
      title: 'Community Governance',
      description: 'Platform updates are voted on and executed by DEX token holders.',
    },
  ],
  footer: {
    description:
      'The next generation cross-chain decentralized exchange. Establish secure compounding liquidity yields within a luxury DeFi system.',
  },
  seo: {
    metaTitle: 'DEX | Cross-Chain Decentralized Exchange',
    metaDescription:
      'Trade, earn, and grow with DEX. A secure cross-chain DEX for swaps, liquidity, farming, and staking.',
    keywords: 'dex, dex, defi, swap, staking, liquidity, farming',
    ogImage: '',
  },
};

function sanitizeText(val: any): any {
  if (typeof val === 'string') {
    return val
      .replace(/DEXOR\s*AI/gi, 'DEX')
      .replace(/STAKE\s+DXOR/gi, 'Stake Tokens')
      .replace(/STAKE\s+DEXOR/gi, 'Stake Tokens')
      .replace(/DXOR\s+staking/gi, 'token staking')
      .replace(/DEXOR\s+staking/gi, 'token staking')
      .replace(/\bDXOR\b/g, 'DEX')
      .replace(/\bDexor\b/g, 'DEX')
      .replace(/\bDEXOR\b/g, 'DEX');
  }
  if (Array.isArray(val)) {
    return val.map(sanitizeText);
  }
  if (val !== null && typeof val === 'object') {
    const res: any = {};
    for (const key of Object.keys(val)) {
      res[key] = sanitizeText(val[key]);
    }
    return res;
  }
  return val;
}

export const parseHomeCmsContent = (content?: any): HomeCmsContent => {
  if (!content) {
    return DEFAULT_HOME_CMS;
  }

  try {
    const rawParsed = typeof content === 'string' ? JSON.parse(content) as Partial<HomeCmsContent> : content as Partial<HomeCmsContent>;
    const parsed = sanitizeText(rawParsed) as Partial<HomeCmsContent>;
    
    if (!parsed || Object.keys(parsed).length === 0) {
      return DEFAULT_HOME_CMS;
    }

    return {
      ...DEFAULT_HOME_CMS,
      ...parsed,
      hero: {
        title: parsed.hero?.title || DEFAULT_HOME_CMS.hero.title,
        highlight: parsed.hero?.highlight || DEFAULT_HOME_CMS.hero.highlight,
        description: parsed.hero?.description || DEFAULT_HOME_CMS.hero.description,
      },
      stakeBanner: {
        title: parsed.stakeBanner?.title || DEFAULT_HOME_CMS.stakeBanner.title,
        description: parsed.stakeBanner?.description || DEFAULT_HOME_CMS.stakeBanner.description,
      },
      walletBanner: {
        title: parsed.walletBanner?.title || DEFAULT_HOME_CMS.walletBanner.title,
        description: parsed.walletBanner?.description || DEFAULT_HOME_CMS.walletBanner.description,
      },
      footer: {
        description: parsed.footer?.description || DEFAULT_HOME_CMS.footer.description,
      },
      seo: {
        metaTitle: parsed.seo?.metaTitle || DEFAULT_HOME_CMS.seo.metaTitle,
        metaDescription: parsed.seo?.metaDescription || DEFAULT_HOME_CMS.seo.metaDescription,
        keywords: parsed.seo?.keywords || DEFAULT_HOME_CMS.seo.keywords,
        ogImage: parsed.seo?.ogImage || DEFAULT_HOME_CMS.seo.ogImage,
      },
      ecosystem: parsed.ecosystem?.length ? parsed.ecosystem : DEFAULT_HOME_CMS.ecosystem,
      features: parsed.features?.length ? parsed.features : DEFAULT_HOME_CMS.features,
    };
  } catch {
    return DEFAULT_HOME_CMS;
  }
};
